import { useLocation, useNavigate } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { URL_PATH_DRILLDOWN_EXPLORATION_VIEWER } from 'features/drilldown'
import React, { useEffect } from 'react'

import { Homebar } from './Homebar'
import { getRoutesRequiringSiteID } from './navConfig'
import { SelectedTab } from './types'
import { useNavbarData } from './useNavbarData'

export const getSelectedTab = (pathname: string): SelectedTab | undefined => {
  if (pathname.includes('/home')) return SelectedTab.Home
  if (pathname.includes('/assets')) return SelectedTab.Assets
  if (pathname.includes('/ships')) return SelectedTab.Ships
  if (pathname.includes('/inspections')) return SelectedTab.Inspections
  if (pathname.includes('/inbox')) return SelectedTab.Inbox
  if (pathname.includes('publisher')) return SelectedTab.Publisher
  if (pathname.includes('/health-plans')) return SelectedTab.HealthPlans
  if (pathname.includes('/budgeting')) return SelectedTab.Budgeting
  if (pathname.includes('/scheduling')) return SelectedTab.InspectionScheduling
  if (pathname.includes('/availability-planning'))
    return SelectedTab.AvailabilityPlanning
  if (pathname.includes(`/overview`)) return SelectedTab.Overview
  if (pathname.includes('/cases')) return SelectedTab.Cases
  if (pathname.includes(URL_PATH_DRILLDOWN_EXPLORATION_VIEWER))
    return SelectedTab.Exploration
  if (pathname.includes('/explorer')) return SelectedTab.Explorer
  if (pathname.includes('/prism')) return SelectedTab.Prism
  if (pathname.includes('/parts')) return SelectedTab.PartsThread
  if (pathname.includes('/jobs')) return SelectedTab.JobsOverview
  if (pathname.includes('/walkdowns')) return SelectedTab.Walkdowns
  if (pathname.includes('/reporting')) return SelectedTab.Reporting
  if (pathname.includes('/inventory')) return SelectedTab.InventoryManagement
  if (pathname.includes('/maximo')) return SelectedTab.Maximo
  if (pathname.includes('/system-lens')) return SelectedTab.SystemLens
  if (pathname.includes('/point-cloud')) return SelectedTab.PointCloud
  if (pathname.includes('/process-flows'))
    return SelectedTab.ProcessFlowDiagrams
  if (pathname.includes('/value-stories')) return SelectedTab.ValueStories
  if (pathname.includes('/import-wizard')) return SelectedTab.ImportWizard
  if (pathname.includes('/process-data')) return SelectedTab.ProcessData
  if (pathname.includes('/walkthrough')) return SelectedTab.Walkthrough
  if (pathname.includes('/anomaly-detection'))
    return SelectedTab.AnomalyDetection
  if (pathname.includes('/documents')) return SelectedTab.Documents
  if (pathname.includes('/libra')) return SelectedTab.Libra
}

const ROUTES_REQUIRING_SITE_ID = getRoutesRequiringSiteID()

export const Navbar: React.FC<{
  orgOrSiteID?: string
  defaultClosed?: boolean
  hideSiteSelector?: boolean
  hideSearchBar?: boolean
}> = ({ orgOrSiteID, hideSiteSelector = false, hideSearchBar = false }) => {
  const navigate = useNavigate()
  const { BASE_PATH } = useRuntimeVariables()
  const location = useLocation()

  const {
    site,
    sites,
    sitesForOrg,
    organization,
    organizations,
    isLoading,
    hasEverLoaded,
  } = useNavbarData(orgOrSiteID)

  const inferredSiteID = site?.id

  const shouldHideSiteSelector = hideSiteSelector

  // Only run this effect when site becomes available or orgOrSiteID changes
  // Not on every pathname change (that causes flickering)
  useEffect(() => {
    // Only run if we have a site but no inferred ID (meaning URL has no site param)
    if (!site || inferredSiteID) {
      return
    }

    const isMatchingBasePath = (
      basePath: string,
      currentPath: string
    ): boolean => {
      const pathsToCheck = [
        basePath,
        `${basePath}/`,
        `/${BASE_PATH}${basePath}`,
        `/${BASE_PATH}${basePath}/`,
        `/forecast${basePath}`,
        `/forecast${basePath}/`,
      ]
      return pathsToCheck.includes(currentPath)
    }

    const matching = ROUTES_REQUIRING_SITE_ID.find((route) =>
      isMatchingBasePath(route.basePath, location.pathname)
    )

    if (matching) {
      navigate({ to: `/${matching.targetPath}/${site.id}` })
    }
    // Only re-run when site changes, orgOrSiteID changes, or we navigate to a different base route
    // Don't run on every pathname change (like /assets/123 -> /assets/123/456)
    // location.pathname intentionally omitted to prevent flickering
  }, [
    BASE_PATH,
    site,
    orgOrSiteID,
    inferredSiteID,
    navigate,
    location.pathname,
  ])

  const selectedTab = getSelectedTab(location.pathname + location.search)

  return (
    <Homebar
      siteID={inferredSiteID}
      hideSiteSelector={shouldHideSiteSelector}
      hideSearchBar={hideSearchBar}
      selectedOrganization={organization}
      organizations={organizations}
      selectedSite={site}
      sites={sites}
      sitesForOrg={sitesForOrg}
      isLoading={isLoading}
      hasEverLoaded={hasEverLoaded}
      selectedTab={selectedTab}
    />
  )
}
